import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { JoinApiError } from '../api/join';
import type { RecordingSnapshot } from '../api/recording';

import { RealtimeClient, realtimeUrl } from './client';
import type { RealtimeStatus, WebSocketLike } from './client';
import { fetchChatHistory } from './history';
import { isAllowedReaction, parseRoomKind, sanitizeChatBody, sanitizeQAText } from './frames';
import type {
  AssignableRole,
  ChatMessage,
  LobbyAdmittedMedia,
  LobbyPendingItem,
  ModerationAction,
  ParticipantRole,
  QAAction,
  QAQuestion,
  RoomKind,
  ServerFrame,
} from './frames';
import { MODERATION_ERROR_CODES } from './frames';

/** Live presence info of one participant, from the WS state/hand/role/joined frames. */
export interface RealtimeParticipant {
  readonly displayName: string;
  readonly role: ParticipantRole;
  readonly handRaised: boolean;
}

/**
 * Where the own connection sits relative to the room's lobby gate (CR-14): none - admitted or
 * the room has no lobby; waiting - lobby_wait received, the handshake holds; denied - a staff
 * member refused the join (4407); timeout - the wait crossed the server's 5-minute deadline
 * (4400 after error{lobby_timeout}). denied/timeout are terminal for the connection; the UI
 * offers the retry, never an automatic rejoin.
 */
export type LobbyPhase = 'none' | 'waiting' | 'denied' | 'timeout';

export type { LobbyPendingItem };

/** You-section of the snapshot. */
export interface RealtimeYou {
  readonly identity: string;
  readonly role: ParticipantRole;
}

/** Connection health as the room UI surfaces it: live flows, reconnecting, gone. */
export type RealtimeHealth = 'connecting' | 'live' | 'reconnecting' | 'closed';

export interface RealtimeRoomState {
  readonly health: RealtimeHealth;
  /** Presence map by identity (the state frame snapshot, kept current by the live frames). */
  readonly participants: ReadonlyMap<string, RealtimeParticipant>;
  readonly you: RealtimeYou | null;
  /** Persisted chat rows, ascending by id; system rows included. */
  readonly messages: readonly ChatMessage[];
  /**
   * Bumped every time an authoritative chat_history window replaces the rows (connect and
   * reconnect). UI consumers use it to tell the persisted history - which never counts as
   * unread - apart from the live chat frames appended between windows.
   */
  readonly historySeq: number;
  readonly hasMore: boolean;
  /** True while a load-older request is in flight. */
  readonly loadingOlder: boolean;
  /** Transient "you are sending too fast" notice; clears itself. */
  readonly rateLimited: boolean;
  /** Transient storage hiccup notice (server-side persistence failed once); clears itself. */
  readonly storageError: boolean;
  /** True when the server closed 4403: removed by a moderator. Terminal. */
  readonly removed: boolean;
  /**
   * The staff end-for-everyone decision (the unified room bars): set by the meeting_ended
   * frame and made terminal by the 4406 close. The screen names who ended the room.
   */
  readonly meetingEnded: { readonly by: string; readonly byName: string } | null;
  /** True when reconnect attempts were exhausted. Terminal. */
  readonly disconnected: boolean;
  /** Last known room recording state (frames + connect-time reconciliation); null when none. */
  readonly recording: RecordingSnapshot | null;
  /**
   * The Q&A board (webinar rooms): the qa_history handshake replay kept current by
   * the live qa frames. Dismissed questions drop out; every roster converges per broadcast.
   */
  readonly questions: readonly QAQuestion[];
  /** Latest speak-grant state by identity (speak_grant frames; webinar rooms). */
  readonly speakGrants: ReadonlyMap<string, boolean>;
  /** Latest board-editor grant state by identity (board_grant frames; ADR 0013). */
  readonly boardGrants: ReadonlyMap<string, boolean>;
  /**
   * The room's board-broadcast switch (the board surface amendment): true while the staff
   * opened the board as the room's shared stage. The handshake snapshot seeds it for a late
   * joiner; the live board_surface frames keep every member in sync.
   */
  readonly boardSurfaceOpen: boolean;
  /**
   * The room's kind (CR-12) from the authoritative state frame; null until the first snapshot
   * lands. The join response seeds the provisional value through initialKind.
   */
  readonly roomKind: RoomKind | null;
  /**
   * The own connection's lobby phase (CR-14). The waiting screen shows while this is waiting;
   * denied/timeout replace the room with the rejected screen.
   */
  readonly lobby: LobbyPhase;
  /**
   * The room's lobby waiting list as this connection sees it (CR-14): staff-only data - the
   * server holds lobby frames from everyone else, so a participant's list stays empty and the
   * moderation surfaces hidden. Locally updated on lobby_waiting/lobby_pending, the staff
   * admit/deny actions (optimistic) and the lobby_resolved broadcast (CR-16) plus the
   * participant_joined announce of an admitted identity.
   */
  readonly lobbyPending: readonly LobbyPendingItem[];
}

export interface RealtimeRoomActions {
  readonly sendChat: (body: string) => boolean;
  readonly sendHand: (raised: boolean) => void;
  readonly sendReaction: (emoji: string) => boolean;
  /** Sends one moderation frame; the empty target means the room-wide mute_all. */
  readonly sendModerate: (action: ModerationAction, target: string) => void;
  /** Sends one role assignment (admin only, per the server matrix). */
  readonly sendRole: (target: string, role: AssignableRole) => void;
  /**
   * Sends one Q&A action: ask (text 1..500 runes), upvote (questionId), or the
   * moderator/admin answer (questionId + optional text) and dismiss (questionId). Returns
   * false when the text violates the contract; permission enforcement stays server-side.
   */
  readonly sendQA: (action: QAAction, payload?: { text?: string; questionId?: number }) => boolean;
  /**
   * Sends one board_update with the changed element snapshots (ADR 0013). Permission is
   * server-side: a non-editor is answered with error{forbidden} and the frame is dropped.
   */
  readonly sendBoardUpdate: (elements: readonly unknown[]) => void;
  /**
   * Sends one board_pointer with the sender's cursor position in scene coordinates (CR-18).
   * Editors only server-side: a non-editor is answered with error{forbidden}; out-of-range
   * coordinates clamp into the board bounds. The caller owns the client-side throttle.
   */
  readonly sendBoardPointer: (x: number, y: number) => void;
  /**
   * Staff-only board-broadcast switch (the board surface amendment): opens or closes the
   * board as the room's shared stage. A non-staff sender is answered error{forbidden}; the
   * hub's board_surface echo drives the local state, so the toggle stays server-authoritative.
   */
  readonly sendBoardSurface: (open: boolean) => void;
  /**
   * Staff-only lobby decisions (CR-14): the identity is matched against the room's waiting
   * list server-side; a non-staff sender is answered error{forbidden}, an unknown or
   * already-resolved one error{invalid_request}. The local list row drops immediately
   * (optimistic); the lobby_resolved broadcast (CR-16) keeps every OTHER staff member's panel
   * in sync and lands harmlessly on the sender's already-empty list.
   */
  readonly sendLobbyAdmit: (identity: string) => void;
  readonly sendLobbyDeny: (identity: string) => void;
  readonly loadOlder: () => Promise<void>;
}

export interface UseRealtimeRoomOptions {
  readonly slug: string;
  /** True once the LiveKit join succeeded and the initial token is available. */
  readonly enabled: boolean;
  /** The join JWT the media join already minted; served to the client's first auth frame. */
  readonly initialToken: string | null;
  /**
   * Provisional own presence seeded from the join response (server identity and role) until the
   * server's state frame lands and remains authoritative afterwards.
   */
  readonly initialIdentity?: string | null | undefined;
  readonly initialRole?: ParticipantRole | undefined;
  /**
   * Provisional room kind seeded from the join response (CR-12) until the state frame lands and
   * remains authoritative afterwards.
   */
  readonly initialKind?: RoomKind | null | undefined;
  /**
   * Mints a fresh token (renews the join token through the remint path); used by every
   * reconnect. A 403 kicked_from_room rejection surfaces as removal, like the WS 4403 close.
   */
  readonly remintToken: () => Promise<string>;
  /** Socket factory override for tests. */
  readonly socketFactory?: ((url: string) => WebSocketLike) | undefined;
  /**
   * Delays (ms) before the consecutive reconnect attempts; tests pass all-zero schedules.
   * Defaults to the client's production escalating backoff (~10 minutes of scheduled attempts
   * per outage, cut short by a browser 'online' event).
   */
  readonly reconnectScheduleMs?: readonly number[] | undefined;
  /** Cooperative moderation receipts: today only "stop_share". */
  readonly onModeration?: ((action: string) => void) | undefined;
  /**
   * The server rejected a moderation or role request (forbidden, invalid_request,
   * moderation_failed); nothing changes locally, the UI shows a transient toast.
   */
  readonly onModerationError?: ((code: string) => void) | undefined;
  /** Every reaction frame (own included), for the floating overlay. */
  readonly onReaction?:
    ((reaction: { readonly identity: string; readonly emoji: string }) => void) | undefined;
  /**
   * The own held join was admitted (CR-15): the deferred media credentials arrived on the
   * lobby_admitted frame. Fanned out to the media layer BEFORE the state update, so the page
   * connects to the SFU with the received credentials and enters the room. Fired only on the
   * own connection's admit - staff decisions for other identities never carry credentials.
   */
  readonly onLobbyAdmitted?: ((media: LobbyAdmittedMedia) => void) | undefined;
  /**
   * Reconciles the room recording state right after a connection's state snapshot lands. The
   * server answers auth with the roster only - mid-recording state is NOT re-seeded on
   * (re)connect (ws.go sends no recording state there) - so the client asks the recordings
   * REST list and adopts the active recording, if any. Called with the connection's current
   * token and the role the state frame just confirmed (the list is moderator/admin gated;
   * participants answer 403 and are skipped locally). A rejection is swallowed:
   * reconciliation is best effort.
   */
  readonly reconcileRecording?:
    ((token: string, role: ParticipantRole) => Promise<RecordingSnapshot | null>) | undefined;
  /**
   * Every speak_grant frame (own included), for the media layer: the granted viewer's client
   * mints its publishing token and reconnects; a revocation stops the microphone.
   */
  readonly onSpeakGrant?:
    ((grant: { readonly identity: string; readonly granted: boolean }) => void) | undefined;
  /**
   * Every board_grant frame (own included, ADR 0013): the target client flips its board
   * between read-only and editable from this callback plus the boardGrants state update.
   */
  readonly onBoardGrant?:
    ((grant: { readonly identity: string; readonly granted: boolean }) => void) | undefined;
  /**
   * The staff end-for-everyone decision (the unified room bars): the media session must die
   * with the room - the SFU room is deleted server-side right behind this frame.
   */
  readonly onMeetingEnded?:
    ((ended: { readonly by: string; readonly byName: string }) => void) | undefined;
  /**
   * One editor's incremental board_update, fanned out to the board canvas (ADR 0013). Element
   * arrays never enter React state - they land in the Excalidraw scene through its imperative
   * API instead.
   */
  readonly onBoardUpdate?:
    | ((update: { readonly identity: string; readonly elements: readonly unknown[] }) => void)
    | undefined;
  /** The persisted scene snapshot delivered once per connection (ADR 0013); same fan-out. */
  readonly onBoardState?:
    | ((state: { readonly elements: readonly unknown[]; readonly updatedBy?: string }) => void)
    | undefined;
  /**
   * The room's coalesced live-cursor batch (CR-18): every OTHER editor's latest position,
   * flushed ~10 Hz. Pointer payloads ride the callback like the board scenes - never React
   * state.
   */
  readonly onBoardPointer?:
    | ((
        pointers: readonly { readonly identity: string; readonly x: number; readonly y: number }[],
      ) => void)
    | undefined;
}

const NOTICE_CLEAR_MS = 3000;

const INITIAL_STATE: RealtimeRoomState = {
  health: 'connecting',
  participants: new Map(),
  you: null,
  messages: [],
  historySeq: 0,
  hasMore: false,
  loadingOlder: false,
  rateLimited: false,
  storageError: false,
  removed: false,
  meetingEnded: null,
  disconnected: false,
  recording: null,
  questions: [],
  speakGrants: new Map(),
  boardGrants: new Map(),
  boardSurfaceOpen: false,
  roomKind: null,
  lobby: 'none',
  lobbyPending: [],
};

function statusToHealth(status: RealtimeStatus): RealtimeHealth {
  switch (status) {
    case 'live':
      return 'live';
    case 'reconnecting':
      return 'reconnecting';
    case 'closed':
      return 'closed';
    default:
      return 'connecting';
  }
}

/** True when the re-mint endpoint refused a kicked identity (403 kicked_from_room). */
function isKickedRemintError(err: unknown): boolean {
  return err instanceof JoinApiError && err.code === 'kicked_from_room';
}

/**
 * Merges one broadcast question into the board: an unknown id inserts, a known id converges
 * field-by-field so the partial upvote ({id, upvotes}) and dismiss ({id, dismissed})
 * broadcasts refresh exactly their columns. The board keeps ascending id order and a
 * no-broadcast input returns the same array reference.
 */
function mergeQuestion(
  questions: readonly QAQuestion[],
  incoming: QAQuestion,
): readonly QAQuestion[] {
  const index = questions.findIndex((question) => question.id === incoming.id);
  if (index < 0) {
    const next = [...questions, incoming];
    next.sort((a, b) => a.id - b.id);
    return next;
  }
  const current = questions[index];
  if (current === undefined) {
    return questions;
  }
  // '' text columns of partial broadcasts never blank out what the roster already shows.
  const merged: QAQuestion = {
    ...current,
    ...incoming,
    identity: incoming.identity.length > 0 ? incoming.identity : current.identity,
    displayName: incoming.displayName.length > 0 ? incoming.displayName : current.displayName,
    text: incoming.text.length > 0 ? incoming.text : current.text,
    ts: incoming.ts.length > 0 ? incoming.ts : current.ts,
    answerText: incoming.answerText ?? current.answerText,
    answeredBy: incoming.answeredBy ?? current.answeredBy,
  };
  if (
    merged.identity === current.identity &&
    merged.displayName === current.displayName &&
    merged.text === current.text &&
    merged.upvotes === current.upvotes &&
    merged.answered === current.answered &&
    merged.answeredBy === current.answeredBy &&
    merged.answerText === current.answerText &&
    merged.dismissed === current.dismissed &&
    merged.ts === current.ts
  ) {
    return questions;
  }
  return [...questions.slice(0, index), merged, ...questions.slice(index + 1)];
}

function applyFrame(state: RealtimeRoomState, frame: ServerFrame): RealtimeRoomState {
  switch (frame.type) {
    case 'state': {
      const participants = new Map<string, RealtimeParticipant>();
      for (const participant of frame.participants) {
        participants.set(participant.identity, {
          displayName: participant.displayName,
          role: participant.role,
          handRaised: participant.handRaised,
        });
      }
      // The snapshot is the admit's first fruit: the hold ends, the room renders. The
      // lobbyPending list is NOT cleared here - the staff handshake delivers its own state
      // frame BEFORE the lobby_pending snapshot (ws.go frame order), so clearing would wipe
      // the list the very frame before it arrives.
      return {
        ...state,
        participants,
        you: { identity: frame.you.identity, role: frame.you.role },
        roomKind: parseRoomKind(frame.kind),
        lobby: state.lobby === 'waiting' ? 'none' : state.lobby,
      };
    }
    case 'chat_history':
      // A fresh authoritative window replaces whatever the previous socket had streamed.
      return {
        ...state,
        messages: [...frame.messages],
        historySeq: state.historySeq + 1,
        hasMore: frame.hasMore,
      };
    case 'chat': {
      if (state.messages.some((message) => message.id === frame.message.id)) {
        return state;
      }
      return { ...state, messages: [...state.messages, frame.message] };
    }
    case 'participant_joined': {
      const participants = new Map(state.participants);
      participants.set(frame.identity, {
        displayName: frame.displayName,
        role: frame.role,
        handRaised: false,
      });
      // A joined identity can no longer be waiting (its admit announced it): drop the lobby
      // row too - this also covers an admit another staff member made, which broadcasts no
      // resolution frame of its own.
      const lobbyPending = state.lobbyPending.filter((item) => item.identity !== frame.identity);
      return { ...state, participants, lobbyPending };
    }
    case 'participant_left': {
      const participants = new Map(state.participants);
      participants.delete(frame.identity);
      return { ...state, participants };
    }
    case 'hand': {
      const current = state.participants.get(frame.identity);
      if (current === undefined) {
        return state;
      }
      const participants = new Map(state.participants);
      participants.set(frame.identity, { ...current, handRaised: frame.raised });
      return { ...state, participants };
    }
    case 'reaction':
      return state;
    case 'role': {
      // A role frame naming the own identity updates `you` as well: the moderation surfaces
      // are derived from the own role, which would otherwise stay at its auth-time value
      // until the server's state resync lands.
      const current = state.participants.get(frame.identity);
      const you =
        state.you !== null && state.you.identity === frame.identity
          ? { ...state.you, role: frame.role }
          : state.you;
      if (current === undefined && you === state.you) {
        return state;
      }
      const participants = new Map(state.participants);
      if (current !== undefined) {
        participants.set(frame.identity, { ...current, role: frame.role });
      }
      return { ...state, participants, you };
    }
    case 'moderation':
      return state;
    case 'meeting_ended':
      return { ...state, meetingEnded: { by: frame.by, byName: frame.byName } };
    case 'recording':
      return { ...state, recording: { state: frame.state, recordingId: frame.recordingId } };
    case 'qa_history':
      // A fresh authoritative board replaces whatever the previous socket had streamed.
      return { ...state, questions: [...frame.questions] };
    case 'qa': {
      const merged = mergeQuestion(state.questions, frame.question);
      if (merged === state.questions) {
        return state;
      }
      return { ...state, questions: merged };
    }
    case 'speak_grant': {
      const grants = new Map(state.speakGrants);
      if (grants.get(frame.identity) === frame.granted) {
        return state;
      }
      grants.set(frame.identity, frame.granted);
      return { ...state, speakGrants: grants };
    }
    case 'board_grant': {
      const grants = new Map(state.boardGrants);
      if (grants.get(frame.identity) === frame.granted) {
        return state;
      }
      grants.set(frame.identity, frame.granted);
      return { ...state, boardGrants: grants };
    }
    case 'board_surface':
      if (state.boardSurfaceOpen === frame.open) {
        return state;
      }
      return { ...state, boardSurfaceOpen: frame.open };
    // Board element payloads ride the callbacks (onBoardUpdate/onBoardState), never React
    // state: scenes are large and imperatively applied into the Excalidraw API.
    case 'board_update':
    case 'board_state':
    case 'board_pointer':
      return state;
    // Lobby frames (CR-14). The denied frame usually precedes the 4407 close; either signal
    // alone must land the rejected screen, so both write the phase.
    case 'lobby_wait':
      return { ...state, lobby: 'waiting' };
    case 'lobby_waiting': {
      if (state.lobbyPending.some((item) => item.identity === frame.item.identity)) {
        return state;
      }
      return { ...state, lobbyPending: [...state.lobbyPending, frame.item] };
    }
    case 'lobby_pending':
      return { ...state, lobbyPending: [...frame.items] };
    case 'lobby_resolved':
      // CR-16: a staff member (possibly another one) decided this identity - admit or deny.
      // Every panel drops the row; an admit's own participant_joined announce would remove it
      // too, so the removal is idempotent from either direction.
      return {
        ...state,
        lobbyPending: state.lobbyPending.filter((item) => item.identity !== frame.identity),
      };
    case 'lobby_admitted':
      // The media layer consumes the credentials through the onLobbyAdmitted fan-out; nothing
      // to store - the state snapshot right after flips the lobby phase.
      return state;
    case 'lobby_denied':
      return { ...state, lobby: 'denied', lobbyPending: [], health: 'closed' };
    case 'error':
      if (frame.code === 'rate_limited') {
        return { ...state, rateLimited: true };
      }
      if (frame.code === 'storage_error') {
        return { ...state, storageError: true };
      }
      if (frame.code === 'lobby_timeout') {
        // The 4400 close follows; the phase is already terminal here so the screen renders
        // even if the close event races past the unmount.
        return { ...state, lobby: 'timeout', health: 'closed' };
      }
      return state;
    default:
      return state;
  }
}

/**
 * Owns the realtime WS client for one joined room and derives the UI state from its frames:
 * presence (roles, hands), the chat history plus live rows, transient rate-limit/storage
 * notices, and the terminal removal/disconnection flags. Callbacks fan moderation receipts and
 * reactions out to the media layer (screen-share stop, floating overlay).
 */
export function useRealtimeRoom(options: UseRealtimeRoomOptions): {
  readonly state: RealtimeRoomState;
  readonly actions: RealtimeRoomActions;
} {
  const {
    slug,
    enabled,
    initialToken,
    initialIdentity,
    initialRole,
    initialKind,
    remintToken,
    socketFactory,
    reconnectScheduleMs,
    onModeration,
    onModerationError,
    onReaction,
    reconcileRecording,
    onSpeakGrant,
    onBoardGrant,
    onMeetingEnded,
    onBoardUpdate,
    onBoardState,
    onBoardPointer,
    onLobbyAdmitted,
  } = options;
  const [state, setState] = useState<RealtimeRoomState>(() => ({
    ...INITIAL_STATE,
    // The join response's identity and role seed the provisional own presence; the state frame
    // replaces them as soon as the server answers (INITIAL_STATE.you stays null without them).
    // The same pattern seeds the provisional room kind from the join response (CR-12).
    you:
      typeof initialIdentity === 'string' && initialIdentity.length > 0
        ? { identity: initialIdentity, role: initialRole ?? 'participant' }
        : null,
    roomKind: initialKind ?? null,
  }));
  const clientRef = useRef<RealtimeClient | null>(null);
  /** Unsubscribes the exhaustion-revive listeners (online/visibilitychange) when wired. */
  const reviveListenersRef = useRef<(() => void) | null>(null);
  const tokenRef = useRef<string | null>(initialToken);
  // A new initial token means a new join (re-join flow): the cached token must follow it.
  // Reconnect re-mints update tokenRef too, but never change the prop, so they are not reset.
  // A fresh join also resets the lobby surfaces (CR-14): the previous attempt's wait or
  // rejection belongs to the previous session. The token - not the identity - is the trigger,
  // because an SSO retry re-joins with the SAME identity and must still clear its denied flag.
  const lastInitialTokenRef = useRef(initialToken);
  if (lastInitialTokenRef.current !== initialToken) {
    lastInitialTokenRef.current = initialToken;
    tokenRef.current = initialToken;
    setState((current) =>
      current.lobby === 'none' && current.lobbyPending.length === 0
        ? current
        : { ...current, lobby: 'none', lobbyPending: [] },
    );
  }
  // Same for a fresh join's provisional identity and kind: the seeded you follows the new join
  // response.
  const lastInitialIdentityRef = useRef(initialIdentity);
  if (lastInitialIdentityRef.current !== initialIdentity) {
    lastInitialIdentityRef.current = initialIdentity;
    setState((current) => ({
      ...current,
      you:
        typeof initialIdentity === 'string' && initialIdentity.length > 0
          ? { identity: initialIdentity, role: initialRole ?? 'participant' }
          : null,
      roomKind: initialKind ?? null,
    }));
  }
  // Latest callbacks in refs so the effect below does not need to re-run (and re-connect) when
  // the parent passes fresh closures. remintToken gets the same treatment: its identity changes
  // whenever the parent's SSO state rotates (the callback closes over the fresh access token),
  // but an SSO token rotation must never re-run this effect - the room session runs on join
  // tokens, and re-creating the realtime client on every rotation would announce a spurious
  // joined/left presence flap (and persisted system chat rows) mid-call.
  const moderationRef = useRef(onModeration);
  const reactionRef = useRef(onReaction);
  const moderationErrorRef = useRef(onModerationError);
  const remintTokenRef = useRef(remintToken);
  const reconcileRecordingRef = useRef(reconcileRecording);
  const speakGrantRef = useRef(onSpeakGrant);
  const boardGrantRef = useRef(onBoardGrant);
  const meetingEndedRef = useRef(onMeetingEnded);
  const boardUpdateRef = useRef(onBoardUpdate);
  const boardStateRef = useRef(onBoardState);
  const boardPointerRef = useRef(onBoardPointer);
  const lobbyAdmittedRef = useRef(onLobbyAdmitted);
  moderationRef.current = onModeration;
  reactionRef.current = onReaction;
  moderationErrorRef.current = onModerationError;
  remintTokenRef.current = remintToken;
  reconcileRecordingRef.current = reconcileRecording;
  speakGrantRef.current = onSpeakGrant;
  boardGrantRef.current = onBoardGrant;
  meetingEndedRef.current = onMeetingEnded;
  boardUpdateRef.current = onBoardUpdate;
  boardStateRef.current = onBoardState;
  boardPointerRef.current = onBoardPointer;
  lobbyAdmittedRef.current = onLobbyAdmitted;

  // One client per (enabled, slug); the initial token is fixed at creation time. Dependent
  // callbacks come through refs.
  useEffect(() => {
    if (!enabled || initialToken === null) {
      return;
    }
    const getToken = async (firstAuth: boolean): Promise<string> => {
      if (firstAuth) {
        const cached = tokenRef.current;
        if (cached !== null) {
          return cached;
        }
      }
      try {
        const fresh = await remintTokenRef.current();
        tokenRef.current = fresh;
        return fresh;
      } catch (err: unknown) {
        if (isKickedRemintError(err)) {
          // 403 kicked_from_room from the re-mint endpoint: the server refuses to renew a
          // kicked identity, so the session is terminal - the same removed flow as the WS
          // 4403 close. stop() ends the reconnect loop the way the kicked close does.
          clientRef.current?.stop();
          setState((current) => ({ ...current, removed: true, health: 'closed' }));
        }
        throw err;
      }
    };
    const client = new RealtimeClient({
      url: realtimeUrl(),
      getToken,
      socketFactory,
      reconnectScheduleMs,
    });
    clientRef.current = client;
    const offStatus = client.on('status', (status) => {
      setState((current) => ({ ...current, health: statusToHealth(status) }));
    });
    const reconcileRecordingNow = (role: ParticipantRole): void => {
      const reconcile = reconcileRecordingRef.current;
      const token = tokenRef.current;
      if (reconcile === undefined || token === null) {
        return;
      }
      reconcile(token, role)
        .then((snapshot) => {
          // Answers of a superseded connection never land in the fresh client's state.
          if (clientRef.current === client) {
            setState((current) => ({ ...current, recording: snapshot }));
          }
        })
        .catch(() => {
          // Best effort: a failed reconciliation (offline, disabled, 403) keeps what frames
          // already delivered.
        });
    };
    const offFrame = client.on('frame', (frame) => {
      if (frame.type === 'reaction') {
        reactionRef.current?.({ identity: frame.identity, emoji: frame.emoji });
        return;
      }
      if (frame.type === 'moderation') {
        if (frame.action === 'stop_share') {
          moderationRef.current?.(frame.action);
        }
        return;
      }
      if (
        frame.type === 'error' &&
        (MODERATION_ERROR_CODES as readonly string[]).includes(frame.code)
      ) {
        moderationErrorRef.current?.(frame.code);
        return;
      }
      if (frame.type === 'speak_grant') {
        // Fan the grant out to the media layer before the roster-wide state update: the
        // target's client mints its publishing token (or stops the mic) from this callback.
        speakGrantRef.current?.({ identity: frame.identity, granted: frame.granted });
      }
      if (frame.type === 'lobby_admitted') {
        // CR-15: the own held join was admitted - hand the deferred media credentials to the
        // page (which connects the SFU with them) before the state snapshot flips the phase.
        lobbyAdmittedRef.current?.(frame.credentials);
      }
      if (frame.type === 'board_update') {
        boardUpdateRef.current?.({ identity: frame.identity, elements: frame.elements });
      }
      if (frame.type === 'board_pointer') {
        boardPointerRef.current?.(frame.pointers);
      }
      if (frame.type === 'board_state') {
        boardStateRef.current?.({
          elements: frame.elements,
          ...(frame.updatedBy !== undefined ? { updatedBy: frame.updatedBy } : {}),
        });
      }
      if (frame.type === 'board_grant') {
        boardGrantRef.current?.({ identity: frame.identity, granted: frame.granted });
      }
      if (frame.type === 'meeting_ended') {
        // The end-for-everyone decision: the media layer tears the session down while the
        // frame-driven state update swaps the page onto the post-room screen.
        meetingEndedRef.current?.({ by: frame.by, byName: frame.byName });
      }
      setState((current) => applyFrame(current, frame));
      if (frame.type === 'state') {
        // The snapshot is in: reconcile the recording state (the server does not re-seed it
        // on auth). The frame's own you.role is authoritative for the gate; later frames keep
        // the reconciled answer current.
        reconcileRecordingNow(frame.you.role);
      }
    });
    const offExhausted = client.on('exhausted', () => {
      setState((current) => ({ ...current, disconnected: true, health: 'closed' }));
      // A backgrounded tab can burn the whole reconnect schedule while it cannot reach the
      // network at all (throttled timers + an offline window): the exhaustion is then about
      // connectivity, not the room. Revive once - the client restarts from closed - when the
      // browser reports connectivity again or the tab becomes visible; the reconnect mints a
      // fresh token (getToken firstAuth=false), so an expired join JWT cannot brick it. A kick
      // (removed) must never revive: that session is terminal.
      let removed = false;
      const offRemoved = client.on('removed', () => {
        removed = true;
      });
      const revive = (): void => {
        window.removeEventListener('online', revive);
        document.removeEventListener('visibilitychange', onVisibility);
        offRemoved();
        if (removed || clientRef.current !== client) {
          return;
        }
        client.start();
        setState((current) => ({
          ...current,
          disconnected: false,
          health: statusToHealth(client.getStatus()),
        }));
      };
      const onVisibility = (): void => {
        if (document.visibilityState === 'visible') {
          revive();
        }
      };
      window.addEventListener('online', revive);
      document.addEventListener('visibilitychange', onVisibility);
      reviveListenersRef.current = () => {
        window.removeEventListener('online', revive);
        document.removeEventListener('visibilitychange', onVisibility);
        offRemoved();
      };
    });
    // The removed listener outlives the effect on purpose: the 4403 close frame can arrive
    // after the media drop flipped the page out of the connected state (which tears this
    // client down), and the removal must still land. The lobby-deny (4407) and the lobby
    // timeout (4400) get the same treatment: terminal closes whose events must survive the
    // teardown race. The listeners die with the client object itself.
    client.on('removed', () => {
      setState((current) => ({ ...current, removed: true, health: 'closed' }));
    });
    client.on('meetingEnded', () => {
      // The frame carried the decision; the 4406 close makes it terminal (no reconnect).
      setState((current) => ({ ...current, health: 'closed' }));
    });
    client.on('denied', () => {
      setState((current) => ({ ...current, lobby: 'denied', lobbyPending: [], health: 'closed' }));
    });
    client.on('lobbyTimeout', () => {
      setState((current) => ({ ...current, lobby: 'timeout', health: 'closed' }));
    });
    client.start();
    return () => {
      offStatus();
      offFrame();
      offExhausted();
      reviveListenersRef.current?.();
      reviveListenersRef.current = null;
      client.stop();
      clientRef.current = null;
    };
  }, [enabled, initialToken, slug, socketFactory, reconnectScheduleMs]);

  // Transient notices clear themselves.
  useEffect(() => {
    if (!state.rateLimited && !state.storageError) {
      return;
    }
    const timer = setTimeout(() => {
      setState((current) => ({ ...current, rateLimited: false, storageError: false }));
    }, NOTICE_CLEAR_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [state.rateLimited, state.storageError]);

  const loadOlder = useCallback(async (): Promise<void> => {
    const token = tokenRef.current;
    if (token === null || !state.hasMore || state.loadingOlder) {
      return;
    }
    // The cursor is the oldest row the user currently sees; the messages dependency keeps this
    // callback honest across renders.
    const before = state.messages.length > 0 ? (state.messages[0]?.id ?? null) : null;
    setState((current) => ({ ...current, loadingOlder: true }));
    try {
      const page = await fetchChatHistory(slug, token, before);
      setState((current) => {
        const known = new Set(current.messages.map((message) => message.id));
        const fresh = page.messages.filter((message) => !known.has(message.id));
        return {
          ...current,
          messages: [...fresh, ...current.messages],
          hasMore: page.hasMore,
          loadingOlder: false,
        };
      });
    } catch {
      setState((current) => ({ ...current, loadingOlder: false }));
    }
  }, [slug, state.hasMore, state.loadingOlder, state.messages]);

  const sendChat = useCallback((body: string): boolean => {
    const clean = sanitizeChatBody(body);
    if (clean === null) {
      return false;
    }
    clientRef.current?.send({ type: 'chat', body: clean });
    return true;
  }, []);

  const sendHand = useCallback((raised: boolean): void => {
    clientRef.current?.send({ type: 'hand', raised });
  }, []);

  const sendReaction = useCallback((emoji: string): boolean => {
    if (!isAllowedReaction(emoji)) {
      return false;
    }
    clientRef.current?.send({ type: 'react', emoji });
    return true;
  }, []);

  const sendModerate = useCallback((action: ModerationAction, target: string): void => {
    clientRef.current?.send({ type: 'moderate', action, target });
  }, []);

  const sendRole = useCallback((target: string, role: AssignableRole): void => {
    clientRef.current?.send({ type: 'role', target, role });
  }, []);

  const sendQA = useCallback(
    (action: QAAction, payload?: { text?: string; questionId?: number }): boolean => {
      // ask requires text (1..500 runes); answer carries it optionally - an invalid trimmed
      // text fails both locally instead of drawing a server error frame.
      const text =
        action === 'ask' || (action === 'answer' && payload?.text !== undefined)
          ? sanitizeQAText(payload?.text ?? '')
          : undefined;
      if (text === null) {
        return false;
      }
      const questionId = payload?.questionId;
      if (
        (action === 'upvote' || action === 'answer' || action === 'dismiss') &&
        (questionId === undefined || !Number.isInteger(questionId))
      ) {
        return false;
      }
      clientRef.current?.send({
        type: 'qa',
        action,
        ...(typeof text === 'string' ? { text } : {}),
        ...(questionId !== undefined ? { questionId } : {}),
      });
      return true;
    },
    [],
  );

  const sendBoardUpdate = useCallback((elements: readonly unknown[]): void => {
    clientRef.current?.send({ type: 'board_update', elements });
  }, []);

  // Live cursor position (CR-18): the caller throttles; the server re-validates and coalesces.
  const sendBoardPointer = useCallback((x: number, y: number): void => {
    clientRef.current?.send({ type: 'board_pointer', x, y });
  }, []);

  // Staff board-broadcast switch (the board surface amendment): the hub's echo drives the
  // state, so the toggle stays server-authoritative.
  const sendBoardSurface = useCallback((open: boolean): void => {
    clientRef.current?.send({ type: 'board_surface', open });
  }, []);

  // Staff lobby decisions (CR-14). The row drops immediately (optimistic); the lobby_resolved
  // broadcast (CR-16) delivers the same removal to every OTHER staff member, so the panels
  // converge regardless of who decided.
  const sendLobbyDecision = useCallback(
    (frame: { type: 'lobby_admit' | 'lobby_deny'; identity: string }): void => {
      clientRef.current?.send(frame);
      setState((current) => ({
        ...current,
        lobbyPending: current.lobbyPending.filter((item) => item.identity !== frame.identity),
      }));
    },
    [],
  );

  const sendLobbyAdmit = useCallback(
    (identity: string): void => {
      sendLobbyDecision({ type: 'lobby_admit', identity });
    },
    [sendLobbyDecision],
  );

  const sendLobbyDeny = useCallback(
    (identity: string): void => {
      sendLobbyDecision({ type: 'lobby_deny', identity });
    },
    [sendLobbyDecision],
  );

  const actions = useMemo<RealtimeRoomActions>(
    () => ({
      sendChat,
      sendHand,
      sendReaction,
      sendModerate,
      sendRole,
      sendQA,
      sendBoardUpdate,
      sendBoardPointer,
      sendBoardSurface,
      sendLobbyAdmit,
      sendLobbyDeny,
      loadOlder,
    }),
    [
      sendChat,
      sendHand,
      sendReaction,
      sendModerate,
      sendRole,
      sendQA,
      sendBoardUpdate,
      sendBoardPointer,
      sendBoardSurface,
      sendLobbyAdmit,
      sendLobbyDeny,
      loadOlder,
    ],
  );

  return { state, actions };
}
